import normalizeUrl from "src/features/HomepageCarouselBanners/utils/normalizeUrl";

const adaptCreateBanner = (
	data: BannerFormState
): Omit<CarouselBanner, "id"> => {
	const isTakeover = data.bannerType === "fullScreenTakeoverBanner";

	// Takeover layout: 0=name, 1=mobile, 2=tablet, 3=button
	// Other layouts: 0=bannerType, 1=image(s), 2=video, 3=contentBox, 4=button, 5=name

	const result = {
		variant: isTakeover ? "standard" : data.bannerStyle,
		template: data.bannerType,
		banner_type: isTakeover
			? ("internal" as "internal" | "sponsored")
			: ((data.steps[0].value?.toLowerCase() || "internal") as
					| "internal"
					| "sponsored"),
		content_color: isTakeover
			? undefined
			: (data.steps[3].fields![0].givenAnswer as BannerVariant | "accent"),
		heading_text: isTakeover ? undefined : data.steps[3].fields![1].givenAnswer,
		content_text: isTakeover ? undefined : data.steps[3].fields![2].givenAnswer,
		button_style: (isTakeover
			? data.steps[3].fields![0].givenAnswer
			: data.steps[4].fields![0].givenAnswer) as BannerVariant,
		button_text: isTakeover
			? data.steps[3].fields![1].givenAnswer
			: data.steps[4].fields![1].givenAnswer,
		button_visible: isTakeover
			? data.steps[3].isStepDisabled
				? false
				: true
			: data.steps[4].isStepDisabled
				? false
				: true,
		button_link: normalizeUrl(
			isTakeover
				? data.steps[3].fields![2].givenAnswer
				: data.bannerStyle === "standard"
					? data.steps[4].fields![2].givenAnswer
					: data.steps[4].fields![3].givenAnswer
		),
		name: isTakeover
			? data.steps[0].fields![0].givenAnswer
			: data.steps[5].fields![0].givenAnswer,
		image_portrait_uuid: isTakeover
			? data.steps[1].fields?.[0].givenAnswer || null
			: data.steps[1].fields?.[0].givenAnswer || null,
		image_landscape_uuid: isTakeover
			? data.steps[2].fields?.[0].givenAnswer || null
			: data.steps[1].fields?.[1]?.givenAnswer || null,
		video_uuid: isTakeover ? null : data.steps[2].value || null,
		published: false
	} as Omit<CarouselBanner, "id">;

	// Campaign style with hashtag link: send feed_id and link_type
	if (data.bannerStyle === "campaign" && data.feedId) {
		result.feed_id = data.feedId;
		result.link_type = "social_feed";
		result.button_link = "";
	}

	// Promotions and takeovers source link data from the flat LinkPicker
	// fields, not from the step-array. Overwrite button_link with the flat
	// value and emit open_in_browser / feed_id when present.
	const isPromotion =
		data.bannerType === "promotionImage" ||
		data.bannerType === "promotionVideo";
	if (isPromotion || isTakeover) {
		result.button_link = data.buttonLink ?? "";
		if (data.openInBrowser !== undefined) {
			result.open_in_browser = data.openInBrowser;
		}
		if (data.linkFeedId) {
			result.feed_id = data.linkFeedId;
			result.button_link = "";
			result.open_in_browser = false;
		}
	}

	// For promotions, derive button visibility from link presence
	if (isPromotion) {
		const hasLink = Boolean(result.button_link) || Boolean(data.linkFeedId);
		result.button_visible = hasLink;
		if (!result.button_text) result.button_text = "Read more";
		if (!result.button_style) result.button_style = "black" as BannerVariant;
	}

	// Don't send incorrect assets
	if (
		data.bannerType === "singleBanner" ||
		data.bannerType === "fullImageBanner" ||
		data.bannerType === "promotionImage" ||
		data.bannerType === "fullScreenTakeoverBanner"
	) {
		delete result.video_uuid;
	}

	if (
		data.bannerType === "videoBanner" ||
		data.bannerType === "promotionVideo"
	) {
		delete result.image_portrait_uuid;
		delete result.image_landscape_uuid;
	}

	if (data.bannerType === "promotionImage") {
		delete result.image_portrait_uuid;
	}

	// Audience groups — opt-in: only attach when the form actually wired a
	// value. Backend treats omitted / null as "no restriction" so this is
	// safe for consumers that don't expose the picker yet.
	if (data.audienceGroupIds !== undefined) {
		result.audience_group_ids = data.audienceGroupIds;
	}

	return result;
};

export default adaptCreateBanner;
